import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { NMForm, Row, Button, TextButton, Col } from "@neumetric/blueprint-lib/components";
import { auditActions, getAuditTypesList, getCreateReportSuccess, getReportFrameworkList } from "../../store";

const firstFile = (val) => {
  const v = Array.isArray(val) ? val[0] : val;
  if (!v) return null;
  if (v instanceof File) return v;
  if (v?.originFileObj instanceof File) return v.originFileObj;
  if (v?.file instanceof File) return v.file;
  if (v?.blob instanceof Blob) return v.blob;
  return null;
};
export const UploadReportScreen = () => {
  const _formRef = React.useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const created = useSelector(getCreateReportSuccess);
  const auditTypes = useSelector(getAuditTypesList) || [];
  // const FRAMEWORK_OPTIONS = useSelector(getReportFrameworkList) || [];
  const [canSubmit, setCanSubmit] = React.useState(false);

  React.useEffect(() => {
    dispatch(auditActions.fetchAuditTypes());
    dispatch(auditActions.fetchReportFrameworkList());
  }, [dispatch]);

  React.useEffect(() => {
    if (!created) return;
    navigate("/A/R");
  }, [created, navigate, dispatch]);

  const getOptions = React.useCallback(
    (attribute) => {
      if (attribute === "audit_type") {
        return auditTypes.map((a) => ({ id: a.key, label: a.label }));
      }
      if (attribute === "framework") {
        return FRAMEWORK_OPTIONS.map((x) => ({ id: x, label: x }));
      }
      return [];
    },
    [auditTypes],
  );

  const handleChange = () => {
    const ok = _formRef.current?.validate?.(false) === true;
    setCanSubmit(ok);
  };

  const buildCreateReportPayload = () => {
    const values = _formRef.current?.getValues?.() || {};
    const file = firstFile(values.report_file);

    const auditTypeVal =
      typeof values.audit_type === "object" ? (values.audit_type?.id ?? values.audit_type?.label) : values.audit_type;

    return {
      mode: "formdata",
      formdata: [
        { key: "ent_report[title]", value: values.title || "", type: "text" },
        {
          key: "ent_report[reportdate]",
          value: values.report_date || "",
          type: "text",
        },
        {
          key: "ent_report[audit_type]",
          value: auditTypeVal || "",
          type: "text",
        },
        {
          key: "ent_report[framework]",
          value: values.framework || "",
          type: "text",
        },
        ...(file ? [{ key: "ent_report[report_file]", value: file, type: "file" }] : []),
      ],
    };
  };

  const handleUpload = () => {
    const payload = buildCreateReportPayload();
    dispatch(
      auditActions.createReport({
        payload,
        onSuccess: () => navigate("/A/R"),
      }),
    );
  };

  return (
    <div className="w-100 h-100 mt-50 ml-30">
      <div className="ff-exo2 fs-20 fw-600 mb-40 text-heading">Upload Report</div>

      <Col className="pl-30 w-100" style={{ maxWidth: "650px" }}>
        <NMForm
          ref={_formRef}
          variant="INFO"
          fields={uploadReportFormFields}
          onChange={handleChange}
          formData={{}}
          getOptions={getOptions}
        />
        <Row className="pl-30 mt-10 gap-10 w-100" justify="end">
          <TextButton icon="CANCEL" label="Cancel" variant="ACTION" onClick={() => navigate(`/A/R`)} />
          <Button icon="SQUARE_NEXT" label="Upload Report" onClick={handleUpload} disabled={!canSubmit} />
        </Row>
      </Col>
    </div>
  );
};

const uploadReportFormFields = [
  // {
  //   label: "Report ID",
  //   attribute: "report_id",
  //   typeRepresentation: "TEXT",
  //   placeholder: "Report ID",
  //   readOnly: true,
  //   labelAlign: "middle",
  //   classes: { root: "w-100 mb-20", label: "w-110-px" },
  // },
  {
    label: "Report Date",
    attribute: "report_date",
    typeRepresentation: "DATE",
    placeholder: "ddd, DD-MMM-YYYY",
    format: "ddd, DD-MMM-YYYY",
    classes: { root: "w-50", label: "w-[10_5rem]" },
    required: true,
    labelAlign: "middle",
    max: new Date().toISOString(),
  },
  {
    label: "Audit Type",
    attribute: "audit_type",
    typeRepresentation: "DROPDOWN",
    placeholder: "Audit Type",
    labelAlign: "middle",
    classes: { root: "w-40", label: "w-100-px" },
    required: true,
  },
  {
    label: "Framework",
    attribute: "framework",
    typeRepresentation: "DROPDOWN",
    placeholder: "Select Framework",
    classes: { root: "w-100", label: "w-[10_5rem]" },
    required: true,
    labelAlign: "middle",
  },
  {
    label: "Title",
    attribute: "title",
    typeRepresentation: "TEXT",
    placeholder: "Title",
    classes: { root: "w-100", label: "w-[10_5rem]" },
    required: true,
    labelAlign: "middle",
  },
  {
    label: "Report",
    attribute: "report_file",
    typeRepresentation: "FILE",
    placeholder: "Attach PDF file",
    accept: "application/pdf",
    classes: { root: "w-100", label: "w-[10rem]" },
    required: true,
    labelAlign: "middle",
  },
];
const FRAMEWORK_OPTIONS = [
  "SOC 2",
  "ISO 27001",
  "EU GDPR 2018",
  "ISO 27701",
  "CSA STAR",
  "HECVAT",
  "FedRAMP",
  "NIST 800-53",
  "NIST AI RMF",
  "ISO 42001",
  "PCI DSS",
  "CCPA 2019",
  "HIPAA 1996",
  "NIST CSF",
  "COPPA",
  "DPDPA",
  "ISO 27017",
  "ISO 27018",
  "ISO 22301",
  "ISO 27032",
  "ISO 27035",
  "ISO 20000-1",
  "NIST SP 800-171 Rev3",
  "CMMC",
  "FISMA 2002",
  "CIS CSC",
  "COBIT",
  "Singapore PDPA",
  "DORA 2023",
  "CRA 2024",
  "BSIMM",
  "OWASP SAMM",
  "ISO 27001:2022",
  "ISMS Audit",
  "Other",
];
